/**
 * Demo Mode Management Utilities
 * 
 * Handles enabling/disabling demo mode for users who explicitly request it.
 * Demo mode allows users to see demo pages and test features, but is disabled by default
 * to ensure users see production-ready pages after sign-in.
 */

import { getSupabaseClient } from "@/lib/supabase/loader";

export interface DemoModeStatus {
  enabled: boolean;
  requestedAt?: string;
}



/**
 * Check if current user has demo mode enabled
 * Demo mode must be explicitly enabled - it's disabled by default
 */
export async function isDemoModeEnabled(): Promise<boolean> {
  try {
    const supabase = await getSupabaseClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return false;

    const { data: profile, error } = await supabase
      .from("profiles")
      .select("demo_mode_enabled")
      .eq("id", user.id)
      .single();

    if (error) {
      console.error("Error checking demo mode:", error);
      return false;
    }

    return profile?.demo_mode_enabled ?? false;
  } catch (err) {
    console.error("Error checking demo mode:", err);
    return false;
  }
}

/**
 * Check if user has demo mode enabled (from profile object)
 * @param profile - User profile object from database
 */
export function isDemoModeEnabledInProfile(profile: { demo_mode_enabled?: boolean }): boolean {
  return profile?.demo_mode_enabled === true;
}

/**
 * Request to enable demo mode for current user
 * This should be called when user explicitly clicks "Try Demo Mode" or similar
 * 
 * @param justification - Why the user is requesting demo mode
 * @returns Result of the request
 */
export async function requestDemoMode(justification?: string): Promise<{
  success: boolean;
  message: string;
}> {
  try {
    const supabase = await getSupabaseClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      return {
        success: false,
        message: "User not authenticated",
      };
    }

    // Call backend endpoint to enable demo mode
    const response = await fetch("/api/v1/user/enable-demo-mode", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        userId: user.id,
        justification: justification || "User requested demo mode access",
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      return {
        success: false,
        message: error.message || "Failed to enable demo mode",
      };
    }

    return {
      success: true,
      message: "Demo mode enabled successfully",
    };
  } catch (err) {
    console.error("Error requesting demo mode:", err);
    return {
      success: false,
      message: err instanceof Error ? err.message : "Unknown error",
    };
  }
}

/**
 * Disable demo mode for current user (admin action)
 * @param justification - Why demo mode is being disabled
 */
export async function disableDemoMode(justification?: string): Promise<{
  success: boolean;
  message: string;
}> {
  try {
    const supabase = await getSupabaseClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      return {
        success: false,
        message: "User not authenticated",
      };
    }

    // Call backend endpoint to disable demo mode
    const response = await fetch("/api/v1/user/disable-demo-mode", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        userId: user.id,
        justification: justification || "Admin disabled demo mode",
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      return {
        success: false,
        message: error.message || "Failed to disable demo mode",
      };
    }

    return {
      success: true,
      message: "Demo mode disabled successfully",
    };
  } catch (err) {
    console.error("Error disabling demo mode:", err);
    return {
      success: false,
      message: err instanceof Error ? err.message : "Unknown error",
    };
  }
}

/**
 * Get demo mode status for a specific user (admin only)
 * @param userId - The user ID to check
 */
export async function getDemoModeStatus(userId: string): Promise<DemoModeStatus | null> {
  try {
    const supabase = await getSupabaseClient();
    const { data: profile, error } = await supabase
      .from("profiles")
      .select("demo_mode_enabled,demo_mode_requested_at")
      .eq("id", userId)
      .single();

    if (error) {
      console.error("Error getting demo mode status:", error);
      return null;
    }

    return {
      enabled: profile?.demo_mode_enabled ?? false,
      requestedAt: profile?.demo_mode_requested_at,
    };
  } catch (err) {
    console.error("Error getting demo mode status:", err);
    return null;
  }
}

/**
 * Check if user is on a demo page
 * Demo pages should only be accessible if demo mode is enabled OR if it's a public demo
 * 
 * @param pathname - Current path
 * @returns True if path is a demo page
 */
export function isDemoPage(pathname: string): boolean {
  return pathname.includes("/demo") || pathname === "/app/plan-demo";
}

/**
 * Redirect users away from demo pages if demo mode is not enabled
 * This should be checked in layout components
 * 
 * @param pathname - Current path
 * @param isDemoEnabled - Whether demo mode is enabled
 * @param isAnonymous - Whether user is anonymous
 * @returns Path to redirect to, or null if no redirect needed
 */
export function getRedirectFromDemoPage(
  pathname: string,
  isDemoEnabled: boolean,
  isAnonymous: boolean
): string | null {
  // Anonymous users can view public demo pages
  if (isAnonymous && pathname === "/app/plan-demo") {
    return null;
  }

  // Authenticated users not in demo mode should not see demo pages
  if (!isAnonymous && !isDemoEnabled && isDemoPage(pathname)) {
    // Redirect to their home page
    return "/app";
  }

  return null;
}
